import { useMemo } from "react";
import { Text, Button, Badge, Flex, Popover } from "@radix-ui/themes";
import { SnippetStatus } from "@/v2/entities/KnowledgeSnippet/KnowledgeSnippet.model";
import { DropdownMenu } from "@/components/shared/dropdown-menu";
import type { DropdownMenuProps } from "@/components/shared/dropdown-menu";
import { TrashCanIcon } from "@/v2/assets/icons/TrashCanIcon";
import { WorkClockIcon } from "@/v2/assets/icons/WorkClockIcon";
import { CheckmarkBadgeIcon } from "@/v2/assets/icons/CheckmarkBadgeIcon";
import { NotVerifiedIcon } from "@/v2/assets/icons/NotVerifiedIcon";
import { DashboardSquareIcon } from "@/v2/assets/icons/DashboardSquareIcon";
import styles from "./SnippetDetail.module.scss";
import { Tooltip } from "@/components/shared/tooltip";
import { AccessLabelsEditor } from "@/features/knowledge-articles/AccessLabelsEditor";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";

interface SnippetDetailHeaderProps {
  status: SnippetStatus;
  isInternal: boolean;
  onVerifySnippet: () => void;
  onSaveChanges: () => Promise<void> | void;
  onDelete: () => void;
  onVersionHistory: () => void;
  hasUnsavedChanges?: boolean;
  isSavingChanges?: boolean;
  isViewingHistoricalVersion?: boolean;
  isCurrentVersionVerified?: boolean;
  isVerifiedVersionAvailable?: boolean;
  snippetId?: string;
  accessLabels?: string[];
  onRefreshSnippet?: () => void | Promise<void>;
}

export function SnippetDetailHeader({
  status,
  isInternal,
  onVerifySnippet,
  onSaveChanges,
  onDelete,
  onVersionHistory,
  hasUnsavedChanges = false,
  isSavingChanges = false,
  isViewingHistoricalVersion = false,
  isCurrentVersionVerified = false,
  isVerifiedVersionAvailable = false,
  snippetId,
  accessLabels,
  onRefreshSnippet,
}: SnippetDetailHeaderProps) {
  const accessLabelsEnabled = useFlag("articleAccessLabels");
  const statusBadge = useMemo(() => {
    if (isCurrentVersionVerified) {
      return (
        <Badge color="green" variant="soft" className={styles.badge}>
          <CheckmarkBadgeIcon size={12} fill="green" />
          <Text size="1" weight="medium">
            Verified
          </Text>
        </Badge>
      );
    }

    return (
      <Badge color="gray" variant="soft">
        <NotVerifiedIcon size={12} />
        <Text size="1" weight="medium">
          Not Verified
        </Text>
      </Badge>
    );
  }, [status, isCurrentVersionVerified]);

  const accessBadge = useMemo(() => {
    return (
      <Badge color={isInternal ? "blue" : "purple"} variant="soft" size="1">
        <Text size="1" weight="medium">
          {isInternal ? "Internal" : "External"}
        </Text>
      </Badge>
    );
  }, [isInternal]);

  const dropdownMenuItems: DropdownMenuProps["items"] = [
    {
      label: "Delete",
      onClick: onDelete,
      icon: <TrashCanIcon size={12} />,
    },
    {
      label: "Version History",
      onClick: onVersionHistory,
      icon: <WorkClockIcon size={12} />,
    },
  ];

  return (
    <Flex justify="between" mb="5">
      <Flex gap="2">
        <Flex align="center" gap="1">
          {statusBadge}
        </Flex>
        <Flex align="center" gap="1">
          {accessBadge}
        </Flex>
        {!isCurrentVersionVerified && isVerifiedVersionAvailable && (
          <Flex align="center" gap="1">
            <CheckmarkBadgeIcon size={14} fill="green" />
            <Text size="1" color="gray">
              Verified Version Available
            </Text>
          </Flex>
        )}
      </Flex>
      <Flex gap="2" align="center">
        {hasUnsavedChanges && (
          <Badge color="orange" variant="soft" size="1">
            Unsaved changes
          </Badge>
        )}
        <Button
          variant="outline"
          size="2"
          highContrast
          disabled={
            !hasUnsavedChanges || isSavingChanges || isViewingHistoricalVersion
          }
          onClick={() => onSaveChanges()}
        >
          <Text size="2">{isSavingChanges ? "Saving..." : "Save"}</Text>
        </Button>
        {snippetId && accessLabelsEnabled && (
          <Popover.Root>
            <Popover.Trigger>
              <Button variant="outline" size="2" highContrast>
                <Text size="2">Prerequisites Labels</Text>
              </Button>
            </Popover.Trigger>
            <Popover.Content minWidth="400px" maxWidth="500px">
              <AccessLabelsEditor
                snippetId={snippetId}
                initialLabels={accessLabels}
                onSave={onRefreshSnippet}
              />
            </Popover.Content>
          </Popover.Root>
        )}
        <Tooltip>
          <Tooltip.Trigger>
            <Button
              variant="outline"
              size="2"
              highContrast
              disabled={isViewingHistoricalVersion}
              onClick={onVerifySnippet}
            >
              <span className={styles.iconWrapper}>
                <CheckmarkBadgeIcon size={12} fill="currentColor" />
              </span>
              <Text size="2">Verify Snippet</Text>
            </Button>
          </Tooltip.Trigger>
          <Tooltip.Content>
            <Text size="2">
              Mark this snippet as verified so AI Agents and the Knowledge Hub
              can use it reliably
            </Text>
          </Tooltip.Content>
        </Tooltip>
        <DropdownMenu items={dropdownMenuItems} align="end">
          <DropdownMenu.Trigger>
            <Button variant="outline" size="2" highContrast>
              <span className={styles.iconWrapper}>
                <DashboardSquareIcon size={12} fill="currentColor" />
              </span>
              <Text size="2">Actions</Text>
            </Button>
          </DropdownMenu.Trigger>
        </DropdownMenu>
      </Flex>
    </Flex>
  );
}
